import React, { useState } from "react";
import { Box, Typography, Button, Modal, Fade } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  surface, textPrimary, textSecondary, textMuted, font, accent, shadowLifted,
} from "../layouts/schoolPortal/schoolPortalTheme";

// A first-time, multi-slide explainer — same shape as the web-frontend's
// AI Learn "TopicIntroPopup" (hand-built illustrations, dot progress,
// Back/Next -> "Got it"), rebuilt with plain CSS transitions instead of
// framer-motion since xair-admin doesn't carry that dependency.
export default function TutorsOnboardingModal({ open, onClose, slides }) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  if (!slides?.length) return null;
  const slide = slides[index];
  const isLast = index === slides.length - 1;

  const handleClose = () => {
    setIndex(0);
    onClose();
  };

  const goNext = () => {
    if (isLast) {
      handleClose();
      return;
    }
    setDirection(1);
    setIndex((i) => i + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setIndex((i) => Math.max(0, i - 1));
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open} timeout={220}>
        <Box
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "min(92vw, 440px)", backgroundColor: surface, borderRadius: "20px",
            boxShadow: shadowLifted, overflow: "hidden", outline: "none",
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 4 }, pb: 3 }}>
            <Box
              key={index}
              sx={{
                animation: `${direction > 0 ? "tutorsSlideInRight" : "tutorsSlideInLeft"} 0.28s cubic-bezier(0.4,0,0.2,1)`,
                "@keyframes tutorsSlideInRight": {
                  from: { opacity: 0, transform: "translateX(16px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
                "@keyframes tutorsSlideInLeft": {
                  from: { opacity: 0, transform: "translateX(-16px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>{slide.illustration}</Box>
              <Typography sx={{ fontSize: "19px", fontWeight: 800, color: textPrimary, fontFamily: font, textAlign: "center", mb: 1 }}>
                {slide.title}
              </Typography>
              <Typography sx={{ fontSize: "13.5px", color: textSecondary, fontFamily: font, textAlign: "center", lineHeight: 1.6, px: 1 }}>
                {slide.description}
              </Typography>
            </Box>
          </Box>

          {/* Progress dots */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mb: 2.5 }}>
            {slides.map((_, i) => (
              <Box
                key={i}
                sx={{
                  height: 6, borderRadius: 999,
                  width: i === index ? 20 : 6,
                  backgroundColor: i === index ? accent : "rgba(26,26,26,0.14)",
                  transition: "width 0.25s ease, background-color 0.25s ease",
                }}
              />
            ))}
          </Box>

          {/* Footer */}
          <Box sx={{ display: "flex", gap: 1, p: { xs: 2.5, sm: 3 }, pt: 0 }}>
            {index > 0 && (
              <Button
                onClick={goBack}
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                sx={{ color: textMuted, textTransform: "none", fontFamily: font, fontWeight: 600, fontSize: "13.5px" }}
              >
                Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={goNext}
              endIcon={!isLast && <ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{
                background: `linear-gradient(135deg, ${accent} 0%, #a81828 100%)`,
                color: "#fff", textTransform: "none", fontFamily: font, fontWeight: 700, fontSize: "13.5px",
                borderRadius: "999px", px: 3, py: 1.1,
                boxShadow: "0 4px 14px rgba(192,30,46,0.32)",
                "&:hover": { boxShadow: "0 6px 18px rgba(192,30,46,0.4)" },
              }}
            >
              {isLast ? "Got it" : "Next"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
